import React, { forwardRef } from 'react';
import { Grid, GridProps, TextField, TextFieldProps } from '@material-ui/core';
import ExtensionField, { ExtFieldProps } from './ExtensionField';
import PhoneField, { PhoneFieldChangeHandler, PhoneFieldProps } from '../PhoneField';
import { AutocompleteInput } from '../AutocompleteInput';

export type PhoneFieldGroupArrayProps = {
  ContainerComponentProps?: GridProps;
  PhoneTypeSelectProps?: TextFieldProps;
  PhoneFieldProps?: PhoneFieldProps;
  ExtFieldProps?: ExtFieldProps;
  value: PhoneType;
  onChange: (value: PhoneType) => void;
  onBlur?: (value: PhoneType) => void;
  error?: string | undefined;
  typeError?: string | undefined;
};

export type PhoneType = {
  [key: string]: string | boolean | undefined;
  name?: string;
  type?: 'Home' | 'Mobile' | 'Business' | 'Office' | 'Fax' | '' | undefined;
  phoneNumber?: string | undefined;
  ext?: string | undefined;
  isPrimary?: boolean;
  id?: string; // Note: ID is only used for frontend development
};

export const mapToPhoneNumbers = (phones: PhoneType[]) => {
  const phoneNumberRequest = [];

  for (const phone of phones) {
    const phoneRequest = {
      type: phone?.type ?? '',
      phoneNumber: phone.phoneNumber,
      ext: phone.ext,
      isPrimary: phone.isPrimary,
    };
    phoneNumberRequest.push(phoneRequest);
  }

  return phoneNumberRequest;
};

export const HOME = 'Home';
export const MOBILE = 'Mobile';
export const BUSINESS = 'Business';
export const OFFICE = 'Office';
export const FAX = 'Fax';
export const EXTENSION_PHONE_TYPES = [OFFICE, FAX, BUSINESS];

const generateDefaultValue = (value?: PhoneType) => {
  return {
    type: '',
    phoneNumber: '',
    ext: '',
    isPrimary: false,
    ...value,
  } as PhoneType;
};

export const PhoneFieldGroupArray = forwardRef<HTMLDivElement, PhoneFieldGroupArrayProps>(
  (
    {
      ContainerComponentProps,
      PhoneTypeSelectProps,
      PhoneFieldProps,
      ExtFieldProps,
      value: valueProp = generateDefaultValue(),
      error,
      typeError,
      onChange = () => undefined,
      onBlur = () => undefined,
    }: PhoneFieldGroupArrayProps,
    ref,
  ): JSX.Element => {
    const typeName = PhoneTypeSelectProps?.name || 'type';
    const phoneNumberName = PhoneFieldProps?.name || 'phoneNumber';
    const extName = ExtFieldProps?.name || 'ext';
    const disabled = PhoneTypeSelectProps?.disabled ?? false;

    const handleChangePhoneType = (value: string | null) => {
      const phoneTypeValue = value ? value : '';
      const newValue = { ...valueProp, [typeName]: phoneTypeValue };

      if (newValue[typeName] == HOME || newValue[typeName] == MOBILE) {
        newValue['ext'] = '';
      }

      onChange(newValue);
    };

    const handlePhoneFieldChange: PhoneFieldChangeHandler = (value, _values) => {
      onChange({ ...valueProp, [phoneNumberName]: value });
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const { name, value } = e.target;
      onChange({ ...valueProp, [name]: value });
    };

    const handleBlur = () => {
      if (onBlur) onBlur(valueProp);
    };

    const mobileOptions: string[] = [HOME, MOBILE, BUSINESS, FAX];

    return (
      <Grid ref={ref} container {...ContainerComponentProps}>
        <Grid item xs={12} sm={3} md={4}>
          <AutocompleteInput
            data-testid="auto-phone-type"
            onChange={(_event, value) => {
              handleChangePhoneType(value as string);
            }}
            options={mobileOptions}
            value={valueProp[typeName]?.toString() ?? ''}
            renderInput={(params) => (
              <TextField
                data-testid="phone-type"
                FormHelperTextProps={{ 'data-testid': 'phone-type-input' } as { [key: string]: string }}
                {...PhoneTypeSelectProps}
                className={PhoneTypeSelectProps?.className}
                value={valueProp[typeName]}
                name={typeName}
                error={!!typeError}
                helperText={typeError}
                onBlur={handleBlur}
                {...params}
              />
            )}
            disabled={disabled}
          />
        </Grid>
        <Grid item xs>
          <PhoneField
            data-testid="phone-field-input"
            inputProps={{ 'data-testid': 'phone-field-number-input' }}
            FormHelperTextProps={
              {
                'data-testid': 'phone-field-error-text',
              } as { [key: string]: string }
            }
            {...PhoneFieldProps}
            fullWidth
            onChange={handlePhoneFieldChange}
            onBlur={handleBlur}
            name={phoneNumberName}
            value={valueProp[phoneNumberName] as string}
            error={!!error}
            helperText={error}
            disabled={disabled}
          />
        </Grid>
        {EXTENSION_PHONE_TYPES.includes(valueProp[typeName] as string) && (
          <Grid item xs={4} sm={4} lg={3}>
            <ExtensionField
              {...ExtFieldProps}
              name={extName}
              value={valueProp[extName] as string}
              onChange={handleChange}
              data-testid="phone-ext-field-input"
            />
          </Grid>
        )}
      </Grid>
    );
  },
);

PhoneFieldGroupArray.displayName = 'PhoneFieldGroupArray';
export default PhoneFieldGroupArray;
